import { API, Storage } from 'aws-amplify';

import { IMPERSONATION_READ_ONLY_MESSAGE, isImpersonating } from './impersonation';

// While impersonating, every write that would reach the backend is rejected here. This is
// the single choke point for the whole app: all GraphQL mutations, REST writes and S3
// uploads go through these Amplify singletons.
export function installImpersonationReadOnlyGuard(): void {
    if (!isImpersonating()) {
        return;
    }
    const api = API as any;
    if (api.__impersonationGuarded) {
        return;
    }
    api.__impersonationGuarded = true;

    const originalGraphql = api.graphql.bind(api);
    api.graphql = (...args: any[]) => {
        const operation = getOperationText(args[0]);
        if (isMutation(operation)) {
            return Promise.reject(blockedError(`GraphQL mutace ${getOperationName(operation)}`));
        }

        return originalGraphql(...args);
    };

    for (const method of ['post', 'put', 'patch', 'del'] as const) {
        if (typeof api[method] !== 'function') {
            continue;
        }
        api[method] = (apiName: string, path: string) =>
            Promise.reject(blockedError(`${method.toUpperCase()} ${apiName}${path}`));
    }

    const storage = Storage as any;
    for (const method of ['put', 'remove', 'copy'] as const) {
        if (typeof storage[method] !== 'function') {
            continue;
        }
        storage[method] = () => Promise.reject(blockedError(`Storage.${method}`));
    }
}

function blockedError(what: string): Error {
    const error = new Error(`${IMPERSONATION_READ_ONLY_MESSAGE} (${what})`);
    console.error(error.message);

    return error;
}

function getOperationText(options: any): string {
    const document = typeof options === 'string' ? options : options?.query;
    if (typeof document === 'string') {
        return document;
    }
    if (typeof document?.loc?.source?.body === 'string') {
        return document.loc.source.body;
    }
    if (Array.isArray(document?.definitions)) {
        return document.definitions.some((definition: any) => definition.operation === 'mutation') ? 'mutation' : '';
    }

    return '';
}

// the documents in this codebase always hold a single operation, so the leading keyword decides
function isMutation(operation: string): boolean {
    return /^\s*(#[^\n]*\n\s*)*mutation\b/.test(operation);
}

function getOperationName(operation: string): string {
    return /mutation\s+([A-Za-z0-9_]+)/.exec(operation)?.[1] ?? 'bez názvu';
}
